import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KEY_TO_FINGER } from '../../constants';

// Keyboard + hand guide drawn as one SVG in "key units" (1 unit = one standard
// key). Keys and hands share that coordinate system, so the hands stay in
// proportion and aligned with the keys at any screen size by construction.

type Pt = [number, number];

interface KeyDef { id: string; w: number; label?: string }
interface KeyRect { id: string; label?: string; x: number; y: number; w: number; cx: number; cy: number }

const ROWS: KeyDef[][] = [
  [...'`1234567890-='].map(id => ({ id, w: 1 })).concat([{ id: 'backspace', w: 2, label: 'backspace' }]),
  [{ id: 'tab', w: 1.5, label: 'tab' }, ...[...'qwertyuiop[]'].map(id => ({ id, w: 1 })), { id: '\\', w: 1.5 }],
  [{ id: 'caps', w: 1.75, label: 'caps lock' }, ...[..."asdfghjkl;'"].map(id => ({ id, w: 1 })), { id: 'enter', w: 2.25, label: 'enter' }],
  [{ id: 'lshift', w: 2.25, label: 'shift' }, ...[...'zxcvbnm,./'].map(id => ({ id, w: 1 })), { id: 'rshift', w: 2.75, label: 'shift' }],
  [
    { id: 'lctrl', w: 1.25, label: 'ctrl' }, { id: 'lalt', w: 1.25, label: 'alt' }, { id: 'lmeta', w: 1.25 },
    { id: ' ', w: 6.25, label: 'space' },
    { id: 'rmeta', w: 1.25 }, { id: 'ralt', w: 1.25, label: 'alt' }, { id: 'menu', w: 1.25 }, { id: 'rctrl', w: 1.25, label: 'ctrl' },
  ],
];

const KEYS: KeyRect[] = ROWS.flatMap((row, r) => {
  let x = 0;
  return row.map(k => {
    const rect = { id: k.id, label: k.label, x, y: r, w: k.w, cx: x + k.w / 2, cy: r + 0.5 };
    x += k.w;
    return rect;
  });
});
const KEY_BY_ID = new Map(KEYS.map(k => [k.id, k]));

const SHIFTED: Record<string, string> = {
  '`': '~', '1': '!', '2': '@', '3': '#', '4': '$', '5': '%', '6': '^', '7': '&', '8': '*', '9': '(', '0': ')',
  '-': '_', '=': '+', '[': '{', ']': '}', '\\': '|', ';': ':', "'": '"', ',': '<', '.': '>', '/': '?',
};
const UNSHIFTED: Record<string, string> = Object.fromEntries(Object.entries(SHIFTED).map(([b, s]) => [s, b]));

// The right hand is the left hand mirrored about the vertical line halfway
// between F and J (and A and ;), which is x = 6.75 on this layout.
const MIRROR_X2 = 13.5;

// Left-hand pose, in key units. Fingertips rest on the home keys; knuckles sit
// below the keyboard so the palm and wrist hang off the bottom, as on a real desk.
interface FingerDef { finger: number; rightFinger: number; home: string; base: Pt; w: [number, number]; bow: number }
const FINGERS: FingerDef[] = [
  { finger: 0, rightFinger: 8, home: 'a', base: [2.5, 4.55], w: [0.66, 0.58], bow: -0.1 },
  { finger: 1, rightFinger: 7, home: 's', base: [3.3, 4.6], w: [0.75, 0.67], bow: -0.04 },
  { finger: 2, rightFinger: 6, home: 'd', base: [4.15, 4.65], w: [0.78, 0.69], bow: 0.02 },
  { finger: 3, rightFinger: 5, home: 'f', base: [4.95, 4.6], w: [0.77, 0.68], bow: 0.08 },
];
const THUMB = { finger: 4, tip: [6.3, 4.45] as Pt, base: [5.15, 5.85] as Pt, w: [0.86, 0.76] as [number, number], bow: 0.12 };
const PALM_PATH =
  'M 2 4.25 C 2.6 4 4.6 4 5.35 4.2 C 5.6 4.6 5.75 5.4 5.55 6.2 C 5.35 6.9 5.1 7.3 5 7.9 ' +
  'L 2.4 7.9 C 2.3 7.2 1.85 6.5 1.8 5.6 C 1.75 4.9 1.85 4.5 2 4.25 Z';

interface Slot { side: 'left' | 'right'; finger: number; restTip: Pt; restBase: Pt; w: [number, number]; bow: number }
const SLOTS: Slot[] = (['left', 'right'] as const).flatMap(side => [
  ...FINGERS.map(f => {
    const home = KEY_BY_ID.get(f.home)!;
    return { side, finger: side === 'left' ? f.finger : f.rightFinger, restTip: [home.cx, home.cy - 0.12] as Pt, restBase: f.base, w: f.w, bow: f.bow };
  }),
  { side, finger: THUMB.finger, restTip: THUMB.tip, restBase: THUMB.base, w: THUMB.w, bow: THUMB.bow },
]);

interface Pose { tip: Pt; base: Pt }

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpPt = (a: Pt, b: Pt, t: number): Pt => [lerp(a[0], b[0], t), lerp(a[1], b[1], t)];

// Closed (or open-at-the-base) outline of a slightly bowed, tapering finger from
// base to tip, with a rounded fingertip. The base is extended into the palm so
// the finger merges with it.
function fingerPath(pose: Pose, w: [number, number], bow: number, closed: boolean): string {
  const [tip, base] = [pose.tip, pose.base];
  const len = Math.hypot(tip[0] - base[0], tip[1] - base[1]) || 1;
  const ux = (tip[0] - base[0]) / len;
  const uy = (tip[1] - base[1]) / len;
  const b: Pt = [base[0] - ux * 0.45, base[1] - uy * 0.45];
  const c: Pt = [(b[0] + tip[0]) / 2 - uy * bow, (b[1] + tip[1]) / 2 + ux * bow];

  const left: Pt[] = [];
  const right: Pt[] = [];
  const N = 14;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const px = (1 - t) * (1 - t) * b[0] + 2 * (1 - t) * t * c[0] + t * t * tip[0];
    const py = (1 - t) * (1 - t) * b[1] + 2 * (1 - t) * t * c[1] + t * t * tip[1];
    let tx = 2 * (1 - t) * (c[0] - b[0]) + 2 * t * (tip[0] - c[0]);
    let ty = 2 * (1 - t) * (c[1] - b[1]) + 2 * t * (tip[1] - c[1]);
    const tl = Math.hypot(tx, ty) || 1;
    tx /= tl; ty /= tl;
    const hw = lerp(w[0], w[1], t) / 2;
    left.push([px - ty * hw, py + tx * hw]);
    right.push([px + ty * hw, py - tx * hw]);
  }
  const f = (p: Pt) => `${p[0].toFixed(3)} ${p[1].toFixed(3)}`;
  const r = (w[1] / 2).toFixed(3);
  let d = `M ${f(left[0])}`;
  for (let i = 1; i <= N; i++) d += ` L ${f(left[i])}`;
  d += ` A ${r} ${r} 0 0 0 ${f(right[N])}`;
  for (let i = N - 1; i >= 0; i--) d += ` L ${f(right[i])}`;
  return closed ? d + ' Z' : d;
}

function resolveTarget(nextChar: string | null): { key: string; finger: number; shiftKey: string | null; shiftFinger: number | null } | null {
  if (!nextChar) return null;
  let key = nextChar;
  let shifted = false;
  if (UNSHIFTED[nextChar]) { key = UNSHIFTED[nextChar]; shifted = true; }
  else if (nextChar !== nextChar.toLowerCase()) { key = nextChar.toLowerCase(); shifted = true; }
  const finger = KEY_TO_FINGER[key];
  if (finger === undefined || !KEY_BY_ID.has(key)) return null;
  if (!shifted || finger === 4) return { key, finger, shiftKey: null, shiftFinger: null };
  // Shift is held by the pinky of the opposite hand.
  return finger <= 3 ? { key, finger, shiftKey: 'rshift', shiftFinger: 8 } : { key, finger, shiftKey: 'lshift', shiftFinger: 0 };
}

const toSlotFrame = (slot: Slot, p: Pt): Pt => (slot.side === 'right' ? [MIRROR_X2 - p[0], p[1]] : p);

interface HandGuideKeyboardProps {
  nextChar: string | null;
  pressedKey: string | null;
}

const HandGuideKeyboard: React.FC<HandGuideKeyboardProps> = ({ nextChar, pressedKey }) => {
  const target = resolveTarget(nextChar);
  const targetKey = target?.key ?? null;
  const shiftKey = target?.shiftKey ?? null;

  const { targets, active } = useMemo(() => {
    const t = resolveTarget(nextChar);
    const reach = new Map<number, string>(); // slot index -> key id it reaches for
    const highlighted = new Set<number>();
    SLOTS.forEach((slot, i) => {
      if (!t) return;
      if (slot.finger === t.finger) {
        highlighted.add(i);
        if (t.finger !== 4) reach.set(i, t.key);
      }
      if (t.shiftFinger !== null && slot.finger === t.shiftFinger && t.shiftKey) {
        highlighted.add(i);
        reach.set(i, t.shiftKey);
      }
    });
    const poses: Pose[] = SLOTS.map((slot, i) => {
      const keyId = reach.get(i);
      if (!keyId) return { tip: slot.restTip, base: slot.restBase };
      const k = KEY_BY_ID.get(keyId)!;
      const tip = toSlotFrame(slot, [k.cx, k.cy - 0.08]);
      const base = lerpPt(slot.restBase, [slot.restBase[0] + tip[0] - slot.restTip[0], slot.restBase[1] + tip[1] - slot.restTip[1]], 0.3);
      return { tip, base };
    });
    return { targets: poses, active: highlighted };
  }, [nextChar]);

  // Ease every finger toward its target pose, so the reaching finger visibly
  // travels to the key and the previous one returns home.
  const [pose, setPose] = useState<Pose[]>(targets);
  const poseRef = useRef(pose);
  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      poseRef.current = targets;
      setPose(targets);
      return;
    }
    let raf = 0;
    let last = performance.now();
    const step = (now: number) => {
      const a = 1 - Math.exp(-Math.min(64, now - last) / 55);
      last = now;
      let moving = false;
      const next = poseRef.current.map((p, i) => {
        const goal = targets[i];
        const tip = lerpPt(p.tip, goal.tip, a);
        const base = lerpPt(p.base, goal.base, a);
        if (Math.hypot(tip[0] - goal.tip[0], tip[1] - goal.tip[1]) < 0.004 && Math.hypot(base[0] - goal.base[0], base[1] - goal.base[1]) < 0.004) return goal;
        moving = true;
        return { tip, base };
      });
      poseRef.current = next;
      setPose(next);
      if (moving) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [targets]);

  const pressed = pressedKey ? (UNSHIFTED[pressedKey] ?? pressedKey.toLowerCase()) : null;
  const handShapes = (side: 'left' | 'right', closed: boolean, only?: Set<number>) =>
    SLOTS.map((slot, i) =>
      slot.side === side && (!only || only.has(i)) ? <path key={i} d={fingerPath(pose[i], slot.w, slot.bow, closed)} /> : null,
    );
  const mirror = `translate(${MIRROR_X2} 0) scale(-1 1)`;

  const keyLabel = (k: KeyRect, color: string) => {
    if (k.label) {
      return k.id === ' '
        ? <text x={k.cx} y={k.cy} fontSize={0.24} fill={color} textAnchor="middle" dominantBaseline="central">{k.label}</text>
        : <text x={k.x + 0.16} y={k.y + 0.76} fontSize={0.2} fill={color}>{k.label}</text>;
    }
    if (!k.id.match(/^[a-z]$/) && SHIFTED[k.id]) {
      return (
        <>
          <text x={k.cx} y={k.cy - 0.17} fontSize={0.26} fill={color} textAnchor="middle" dominantBaseline="central">{SHIFTED[k.id]}</text>
          <text x={k.cx} y={k.cy + 0.19} fontSize={0.3} fill={color} textAnchor="middle" dominantBaseline="central">{k.id}</text>
        </>
      );
    }
    if (k.id.length !== 1) return null;
    return (
      <>
        <text x={k.cx} y={k.cy} fontSize={0.42} fill={color} textAnchor="middle" dominantBaseline="central">{k.id}</text>
        {(k.id === 'f' || k.id === 'j') && <line x1={k.cx - 0.12} x2={k.cx + 0.12} y1={k.cy + 0.3} y2={k.cy + 0.3} stroke={color} strokeWidth={0.03} strokeLinecap="round" />}
      </>
    );
  };

  const highlightKeys = [targetKey, shiftKey].filter((id): id is string => !!id).map(id => KEY_BY_ID.get(id)!);

  return (
    <svg
      viewBox="-0.25 -0.25 15.5 7.85"
      className="block w-full mx-auto mt-4 select-none"
      style={{ maxWidth: 760, fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}
      role="img"
      aria-label={targetKey ? `Next key: ${targetKey === ' ' ? 'space' : targetKey}` : 'Keyboard'}
    >
      <rect x={-0.2} y={-0.2} width={15.4} height={5.4} rx={0.22} fill="#eef0f3" stroke="#e2e5e9" strokeWidth={0.03} />

      {highlightKeys.map(k => (
        <circle key={`glow-${k.id}`} cx={k.cx} cy={k.cy} r={0.85} fill="#3b82f6" opacity={0.16} />
      ))}
      {KEYS.map(k => (
        <rect
          key={k.id}
          x={k.x + 0.05} y={k.y + 0.05} width={k.w - 0.1} height={0.9} rx={0.1}
          fill={pressed === k.id ? '#e5e7eb' : '#ffffff'} stroke="#d4d8de" strokeWidth={0.025}
        />
      ))}

      {/* Hands. Drawn twice -- outlines, then fills on top -- inside one
          translucent group, so overlapping finger/palm shapes merge into a
          single silhouette and the keys stay faintly visible underneath. */}
      <g opacity={0.9}>
        <g fill="#f4f4f5" stroke="#a1a1aa" strokeWidth={0.1} strokeLinejoin="round">
          <path d={PALM_PATH} />{handShapes('left', true)}
          <g transform={mirror}><path d={PALM_PATH} />{handShapes('right', true)}</g>
        </g>
        <g fill="#f4f4f5">
          <path d={PALM_PATH} />{handShapes('left', true)}
          <g transform={mirror}><path d={PALM_PATH} />{handShapes('right', true)}</g>
        </g>
      </g>
      <g fill="none" stroke="#3b82f6" strokeWidth={0.07} strokeLinejoin="round" strokeLinecap="round">
        {handShapes('left', false, active)}
        <g transform={mirror}>{handShapes('right', false, active)}</g>
      </g>

      {highlightKeys.map(k => (
        <rect key={`hl-${k.id}`} x={k.x + 0.05} y={k.y + 0.05} width={k.w - 0.1} height={0.9} rx={0.1} fill="#3b82f6" opacity={0.95} />
      ))}
      {KEYS.map(k => (
        <g key={`label-${k.id}`}>{keyLabel(k, highlightKeys.includes(k) ? '#ffffff' : k.label ? '#9ca3af' : '#6b7280')}</g>
      ))}
    </svg>
  );
};

export default HandGuideKeyboard;
